import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { COLORS } from '@scope/scope-one-ui/theme';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ActionsButton, Datatable } from 'ui';
import { theme } from 'theme';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { LinkButton } from 'ui/Buttons';
import { useFeatures, usePortfolioModals, useSelectedTableRows, useTable } from 'shared/hooks';
import { SelectedEntities } from 'shared/types/portfolios';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { exportIndustries } from 'services/export/exportService';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { MUIDataTableMeta } from 'mui-datatables';
import LockIcon from '@mui/icons-material/Lock';
import Typography from '@mui/material/Typography';
import { useDeleteInstruments, useFetchPortfolioInstruments } from '../../GraphQL/portfolios/hooks';
import { makeInstrumentsTableData } from '../utils';
import {
  PortfolioItemsProps,
  PortfolioAction,
  FiltersData,
  InstrumentsInitialFiltersData,
  PortfolioType,
  CollectionType,
} from '../types';
import { DatatableChipFilters } from './components/DatatableChipFilters';
import { DatatableInstrumentsFilters } from './components/DatatableInstrumentsFilters';
import { fetchInitialFiltersData } from './services/filtersService';

const useStyles = makeStyles(() => ({
  root: {
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  buttonLink: {
    cursor: 'pointer',
  },
}));

export const PortfolioInstruments: FC<PortfolioItemsProps> = ({
  portfolioId,
  total = 0,
  refetchPortfolio,
  isReferencePortfolio,
  portfolioData,
  totalCounter,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [updating, setUpdating] = useState(false);
  const {
    singleSelect,
    multiSelect,
    currentSelect,
    selectedAll,
    onSelectAll,
    onSelectedChange,
    selectSingle,
  } = useSelectedTableRows();
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });
  const deleteInstruments = useDeleteInstruments();
  const { error, success } = useSnackbar();
  const [variant, setVariant] = useState<PortfolioAction>();
  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const features = useFeatures();
  const [filtersData, setFiltersData] = useState<FiltersData>({});
  const [filterFieldsInitialData, setFilterFieldsInitialData] =
    useState<InstrumentsInitialFiltersData>();

  const { isDedicated } = portfolioData;

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const deleteInstrument = () => {
    setUpdating(true);
    deleteInstruments({
      instrumentsIds: selectedAll ? [] : Object.values(currentSelect.itemIds),
      portfolioId,
      selectedAll,
    })
      .then(({ data }) =>
        refreshList(true, refreshReasons.DELETED).finally(() =>
          success(data?.deleteInstruments?.message || t('Success')),
        ),
      )
      .catch(() => error(t('Unable to delete entities.')))
      .finally(() => setUpdating(false));
  };

  const copyInstrument = () => {
    setVariant(selectedAll ? 'copy_all' : 'copy');
    addToPortfolio.toggle();
  };

  const moveInstrument = () => {
    setVariant(selectedAll ? 'move_all' : 'move');
    addToPortfolio.toggle();
  };

  const exportToExcel = () => {
    const items = Object.values(currentSelect.itemIds);

    // eslint-disable-next-line no-shadow
    const onError = (err: string) => error(err);

    exportIndustries({
      items: selectedAll ? [] : items,
      onError,
      portfolioId,
      selectedAll,
    });
  };

  /* ******************************************************************** */
  /*                         Datatable filters                            */
  /* ******************************************************************** */

  useEffect(() => {
    if (features.popoverFiltersPortfolioManager) {
      fetchInitialFiltersData(portfolioId, CollectionType.INSTRUMENTS).then((response) =>
        setFilterFieldsInitialData(response),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltersSubmit = (data: FiltersData) => {
    setFiltersData(data);
  };

  const handleDeleteFilter = (propertyName: keyof FiltersData) => {
    // eslint-disable-next-line camelcase
    if (filtersData) {
      // eslint-disable-next-line camelcase
      const { [propertyName]: _removedProperty, ...filtersEdited } = filtersData!;
      setFiltersData(filtersEdited);
    }
  };

  const handleClearAllFilters = () => setFiltersData({});

  const instrumentsFilters = filterFieldsInitialData && (
    <DatatableInstrumentsFilters
      filterFieldsInitialData={filterFieldsInitialData}
      prevFiltersValues={filtersData}
      handleSubmit={handleFiltersSubmit}
    />
  );

  const filterChips = filtersData && (
    <DatatableChipFilters
      filtersData={filtersData}
      handleDelete={handleDeleteFilter}
      handleClearAll={handleClearAllFilters}
    />
  );

  /* ******************************************************************** */
  /*                         Custom body renders                          */
  /* ******************************************************************** */

  const nameRender = (name: string, { rowData: [[, artifactId]] }: MUIDataTableMeta) => (
    <LinkButton to={`/instruments/${artifactId}`} label={name.toUpperCase()} />
  );

  const actionRender = (_: unknown, { rowData: [[id]] }: MUIDataTableMeta) => (
    <ActionsButton onOpen={() => selectSingle(id)} actions={getActions(singleSelect)} />
  );

  const instrumentRatingRender = (rating: string) =>
    rating === 'lock' ? (
      <IconButton color="inherit" size="large">
        <LockIcon />
      </IconButton>
    ) : (
      rating
    );

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'id',
        options: {
          display: false,
        },
      },
      {
        name: 'name',
        label: t('Available entities'),
        options: {
          customBodyRender: nameRender,
        },
      },
      {
        name: 'isin',
        label: t('ISIN'),
      },
      {
        name: 'entity',
        label: t('Entity'),
      },
      {
        name: 'ratingType',
        label: t('Rating type'),
      },
      {
        name: 'demandType',
        label: t('Demand type'),
      },
      {
        name: 'instrumentRating',
        label: t('Instrument\n rating'),
        options: {
          customBodyRender: instrumentRatingRender,
        },
      },
      {
        name: 'maturityDate',
        label: t('Maturity date'),
      },
      {
        name: 'coupon',
        label: t('Coupon'),
      },
      {
        name: 'instrumentVolume',
        label: t('Instrument volume'),
      },
      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleSelect.count, currentSelect.itemIds],
  );

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const isSelectedAllInReferencePortfolio = isReferencePortfolio && selectedAll;

  const getActions = (items: SelectedEntities) => [
    {
      text: 'Copy to portfolio',
      handler: copyInstrument,
      hidden: items.count === 0,
      disabled:
        isSelectedAllInReferencePortfolio ||
        portfolioData.portfolioType === PortfolioType.SAMPLE_PORTFOLIO,
    },
    {
      text: 'Move to portfolio',
      handler: moveInstrument,
      hidden: items.count === 0 || isReferencePortfolio,
      disabled: isDedicated,
    },
    {
      text: 'Export to Excel',
      handler: exportToExcel,
      hidden: items.count === 0,
    },
    {
      text: 'Delete',
      handler: deleteInstrument,
      hidden: items.count === 0 || isReferencePortfolio,
      color: theme.palette.error.main,
      confirmation: {
        title: 'Deleting instrument',
        text: 'Do you really want to delete that instrument?',
      },
      disabled: isDedicated,
    },
  ];

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */
  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
  } as any);

  const { data, loading, refetch } = useFetchPortfolioInstruments(
    portfolioId,
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: pagination.sort.sortColumn,
        direction: pagination.sort.direction,
      },
    ],
    filtersData,
  );

  const { instruments, totalInstruments: filteredInstrumentsCount } = data || {
    instruments: [],
    totalInstruments: 0,
  };

  const refreshList = async (refresh: boolean, reason: string) => {
    setUpdating(true);
    if (refetchPortfolio) {
      refetchPortfolio();
    }
    await refetch();
    setShouldRefresh({ value: refresh, reason });
    setUpdating(false);
  };

  const tableData = useMemo(() => makeInstrumentsTableData(instruments), [instruments]);

  const filtersCounter = Object.keys(filtersData).length;

  const InstrumentsTitle = () => (
    <Box ml={2} display="flex" alignItems="center" gap={2}>
      <h3>{filteredInstrumentsCount} Instruments</h3>
      {!!filtersCounter && (
        <Typography variant="body1" color={COLORS.typography.main}>
          {filtersCounter} {filtersCounter > 1 ? 'Filters' : 'Filter'} applied
        </Typography>
      )}
    </Box>
  );

  useEffect(() => {
    if (totalCounter) {
      totalCounter(filteredInstrumentsCount);
    }
  }, [instruments]);

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <Box className={classes.root}>
      <Datatable
        loading={loading || updating}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={filteredInstrumentsCount || total}
        onSelectedChange={onSelectedChange}
        toolbarData={getActions(multiSelect)}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        selectedAll={selectedAll}
        onSelectAllClick={onSelectAll}
        shouldRefresh={shouldRefresh}
        removeShadow
        leftToolbar={<InstrumentsTitle />}
        showSelectionExtension
        popoverFilters={features.popoverFiltersPortfolioManager ? instrumentsFilters : <></>}
        filterChips={filterChips}
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        onActionSuccess={refreshList}
        itemsData={currentSelect}
        variant={variant}
        itemType="INSTRUMENT"
        sourcePortfolioId={portfolioId}
      />
      <CreateNewPortfolioModal
        itemsData={currentSelect}
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemType="INSTRUMENT"
        actionVariant={variant}
        sourcePortfolioId={portfolioId}
      />
    </Box>
  );
};
